// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useListFocus.ts
 * @input Uses React useCallback, useRef, useIsomorphicLayoutEffect
 * @output Exports useListFocus hook for linear list keyboard navigation
 * @position Core hook; used by TabMenu for dropdown menu navigation, Toolbar
 *   for roving tabindex, ButtonGroup, ContextMenu, NavHeadingMenu, and more.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/hooks/index.ts
 */
import { useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect.js";

/**
 * Navigation orientation for a linear list.
 * - `'horizontal'`: ArrowLeft/ArrowRight move between items.
 * - `'vertical'`: ArrowUp/ArrowDown move between items.
 * - `'both'`: all four arrows move between items (in linear DOM order).
 */

/**
 * Configuration for list focus behavior
 */

/**
 * Return type for useListFocus hook
 */

// Text-editing inputs whose caret must not be hijacked by arrow navigation.
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'email', 'password', 'number']);

/**
 * The nearest `contenteditable` root for `el`, or null when `el` is not inside
 * an editable region. Prefers the browser's `isContentEditable` property and
 * falls back to the closest `[contenteditable]` ancestor whose value is not
 * `"false"` (so environments without `isContentEditable` still work).
 */
function getContentEditableRoot(el) {
  if (el.isContentEditable) {
    return el;
  }
  const candidate = el.closest('[contenteditable]');
  if (candidate && candidate.getAttribute('contenteditable') !== 'false') {
    return candidate;
  }
  return null;
}

/**
 * Whether an arrow/Home/End key should be left to the browser because the
 * event target is a text-editing element whose caret is not yet at the boundary
 * in the direction of travel (or a selection is present). Returns true when the
 * list should NOT steal the key.
 *
 * Covers three editing surfaces:
 * - `<textarea>` and text-type `<input>` — use `selectionStart`/`selectionEnd`
 *   to steal only at the boundary in the travel direction.
 * - `contenteditable` (rich-text editor / chat composer) — precise caret
 *   boundary detection in an arbitrary editable subtree is fragile, so we err
 *   on the side of never hijacking an active editor: defer on every caret key
 *   whenever focus is inside a non-empty contenteditable. (An empty editable
 *   has nothing to caret through, so navigation may proceed.)
 */
function shouldDeferToCaret(target, key) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  // contenteditable (covers an element with contenteditable="" / "true" and
  // descendants that inherit it). `isContentEditable` is the reliable property
  // in the browser; fall back to the nearest `contenteditable` ancestor for
  // environments (e.g. jsdom) that don't implement the property.
  const editableRoot = getContentEditableRoot(target);
  if (editableRoot) {
    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    // A non-collapsed selection means the user is selecting text — never steal.
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      return true;
    }
    // Collapsed caret (or no selection API): defer whenever the editor has
    // content, so arrow keys stay with the active rich-text editor.
    return (editableRoot.textContent ?? '').length > 0;
  }
  const isTextarea = target.tagName === 'TEXTAREA';
  const isTextInput = target.tagName === 'INPUT' && TEXT_INPUT_TYPES.has(target.type);
  if (!isTextarea && !isTextInput) {
    return false;
  }
  const input = target;
  const {
    selectionStart,
    selectionEnd,
    value
  } = input;
  // A non-collapsed selection means the user is selecting text — never steal.
  if (selectionStart !== selectionEnd) {
    return true;
  }
  // number inputs report null selection; treat as "always defer" for caret keys.
  if (selectionStart == null) {
    return true;
  }
  if (key === 'ArrowLeft' || key === 'ArrowUp' || key === 'Home') {
    // Defer unless the caret is at the very start.
    return selectionStart > 0;
  }
  if (key === 'ArrowRight' || key === 'ArrowDown' || key === 'End') {
    // Defer unless the caret is at the very end.
    return selectionStart < value.length;
  }
  return false;
}

/**
 * Hook for managing keyboard navigation within a linear list.
 *
 * Implements WAI-ARIA menu/listbox/toolbar pattern:
 * - ArrowDown/ArrowRight: Move to next item (wraps to first)
 * - ArrowUp/ArrowLeft: Move to previous item (wraps to last)
 * - Home: Move to first item
 * - End: Move to last item
 * - Escape: Custom callback (e.g., close menu)
 *
 * By default the hook only *moves* focus and leaves `tabindex` management to
 * the caller. Opt into {@link UseListFocusOptions.hasRovingTabIndex} for a hook
 * that owns a single tab stop (roving tabindex) across the items — stamping and
 * repairing it as items mount/unmount or toggle disabled — for toolbars,
 * segmented controls, tab strips, and similar composite widgets.
 *
 * @example
 * ```
 * const {listRef, handleKeyDown} = useListFocus({
 *   onEscape: () => layer.hide(),
 * });
 *
 * <div ref={listRef} role="menu" onKeyDown={handleKeyDown}>
 *   {items.map(item => <div role="menuitem" tabIndex={0}>{item}</div>)}
 * </div>
 * ```
 *
 * Roving-tabindex composite (e.g. a toolbar):
 *
 * @example
 * ```
 * const {listRef, handleKeyDown, handleFocus} = useListFocus<HTMLDivElement>({
 *   itemSelector: 'button, input, [tabindex]',
 *   orientation: 'horizontal',
 *   hasRovingTabIndex: true,
 *   hasCaretGuard: true,
 * });
 *
 * <div ref={listRef} role="toolbar" onKeyDown={handleKeyDown} onFocus={handleFocus}>
 *   {children}
 * </div>
 * ```
 */
export function useListFocus(options = {}) {
  const {
    itemSelector = '[role="menuitem"]',
    wrap = true,
    onEscape,
    orientation = 'vertical',
    hasHomeEnd = true,
    isRtl = false,
    hasRovingTabIndex = false,
    hasCaretGuard = false
  } = options;
  const listRef = useRef(null);

  /**
   * Whether an item is disabled and therefore cannot receive DOM focus.
   * A `.focus()` call on such an element silently no-ops, so navigation must
   * skip these to avoid freezing on a disabled item (menus-4, navigation-5).
   */
  const isItemDisabled = useCallback(el => {
    return el.getAttribute('aria-disabled') === 'true' || el.disabled === true || el.hasAttribute('disabled');
  }, []);

  /**
   * Get all focusable items in the list.
   */
  const getItems = useCallback(() => {
    if (!listRef.current) {
      return [];
    }
    return Array.from(listRef.current.querySelectorAll(itemSelector));
  }, [itemSelector]);

  /**
   * Find the next enabled item index from `start`, moving by `step`, optionally
   * wrapping. Returns -1 when no enabled item exists in range. Skipping
   * disabled items here (rather than relying on the selector) keeps navigation
   * from stalling on an item whose `.focus()` silently no-ops.
   */
  const findEnabledIndex = useCallback((items, start, step, shouldWrap) => {
    const count = items.length;
    if (count === 0) {
      return -1;
    }
    let index = start;
    for (let i = 0; i < count; i++) {
      if (index < 0 || index >= count) {
        if (!shouldWrap) {
          return -1;
        }
        index = (index + count) % count;
      }
      const item = items[index];
      if (item && !isItemDisabled(item)) {
        return index;
      }
      index += step;
    }
    return -1;
  }, [isItemDisabled]);

  /**
   * Get the currently focused item index.
   */
  const getCurrentIndex = useCallback(() => {
    const items = getItems();
    const active = document.activeElement;
    return items.findIndex(item => item === active || item.contains(active));
  }, [getItems]);

  // --- Roving tabindex ownership (opt-in via `hasRovingTabIndex`) -------------

  /**
   * Set `tabindex` on an item, but only when it differs (avoids redundant DOM
   * writes). Uses setAttribute so the value reflects even for elements (like
   * `<button>`) whose default tabIndex is already 0.
   */
  const setTabIndex = useCallback((el, value) => {
    if (el.getAttribute('tabindex') !== String(value)) {
      el.setAttribute('tabindex', String(value));
    }
  }, []);

  /**
   * Stamp the roving tab stop: exactly one enabled item is tabbable (0), the
   * rest are -1. Prefer keeping the currently-tabbable item if it is still
   * enabled; otherwise promote the first enabled item (tab-stop repair).
   */
  const syncTabStops = useCallback(() => {
    const items = getItems();
    const enabled = items.filter(el => !isItemDisabled(el));
    if (enabled.length === 0) {
      return;
    }
    const current = enabled.find(el => el.getAttribute('tabindex') === '0');
    const tabbable = current ?? enabled[0];
    for (const el of items) {
      setTabIndex(el, el === tabbable ? 0 : -1);
    }
  }, [getItems, isItemDisabled, setTabIndex]);

  // Keep the tab stop valid across renders (items added/removed, disabled
  // toggled). Runs after every commit but only when roving tabindex is on.
  useIsomorphicLayoutEffect(() => {
    if (hasRovingTabIndex) {
      syncTabStops();
    }
  });

  /**
   * Move focus to `items[index]`. When roving tabindex is enabled, also move
   * the tab stop so `index` becomes the sole tabbable item.
   */
  const focusIndex = useCallback((items, index) => {
    const target = items[index];
    if (!target) {
      return;
    }
    if (hasRovingTabIndex) {
      for (const el of items) {
        setTabIndex(el, el === target ? 0 : -1);
      }
    }
    target.focus();
  }, [hasRovingTabIndex, setTabIndex]);

  /**
   * Focus an item by index, clamping to valid range.
   */
  const focusItem = useCallback(index => {
    const items = getItems();
    if (items.length === 0) {
      return;
    }
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    focusIndex(items, clampedIndex);
  }, [getItems, focusIndex]);

  /**
   * Focus the first enabled item. Returns true when an item was focused.
   */
  const focusFirst = useCallback(() => {
    const items = getItems();
    const index = findEnabledIndex(items, 0, 1, false);
    if (index !== -1) {
      focusIndex(items, index);
      return true;
    }
    return false;
  }, [getItems, findEnabledIndex, focusIndex]);

  /**
   * Focus the last enabled item. Returns true when an item was focused.
   */
  const focusLast = useCallback(() => {
    const items = getItems();
    const index = findEnabledIndex(items, items.length - 1, -1, false);
    if (index !== -1) {
      focusIndex(items, index);
      return true;
    }
    return false;
  }, [getItems, findEnabledIndex, focusIndex]);

  /**
   * Keep the roving stop pointing at whatever ended up focused (e.g. a click
   * or programmatic focus) so the next Tab behaves correctly. No-op unless
   * roving tabindex is enabled.
   */
  const handleFocus = useCallback(() => {
    if (hasRovingTabIndex) {
      syncTabStops();
    }
  }, [hasRovingTabIndex, syncTabStops]);

  /**
   * Handle keyboard navigation.
   */
  const handleKeyDown = useCallback(e => {
    // Let browser/OS shortcut chords (Ctrl/Cmd/Alt + key) through untouched.
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }

    // Escape is handled regardless of orientation. Preserve the historical
    // behavior of always consuming Escape here (preventDefault) so consumers
    // that relied on it are unaffected.
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape?.();
      return;
    }
    const horizontal = orientation === 'horizontal' || orientation === 'both';
    const vertical = orientation === 'vertical' || orientation === 'both';

    // Resolve which keys advance vs retreat, honoring RTL for horizontal.
    const nextKeys = [];
    const prevKeys = [];
    if (horizontal) {
      nextKeys.push(isRtl ? 'ArrowLeft' : 'ArrowRight');
      prevKeys.push(isRtl ? 'ArrowRight' : 'ArrowLeft');
    }
    if (vertical) {
      nextKeys.push('ArrowDown');
      prevKeys.push('ArrowUp');
    }
    const isNext = nextKeys.includes(e.key);
    const isPrev = prevKeys.includes(e.key);
    const isHome = hasHomeEnd && e.key === 'Home';
    const isEnd = hasHomeEnd && e.key === 'End';
    if (!isNext && !isPrev && !isHome && !isEnd) {
      return;
    }

    // Never hijack a caret key from a nested text input mid-line. Check both
    // the React event target and the actual focused element (events may
    // bubble up to the container).
    if (hasCaretGuard && (shouldDeferToCaret(e.target, e.key) || shouldDeferToCaret(document.activeElement, e.key))) {
      return;
    }
    const currentIndex = getCurrentIndex();
    const items = getItems();
    if (isNext) {
      const from = currentIndex === -1 ? 0 : currentIndex + 1;
      const next = findEnabledIndex(items, from, 1, wrap);
      if (next !== -1) {
        focusIndex(items, next);
      }
    } else if (isPrev) {
      const from = currentIndex === -1 ? items.length - 1 : currentIndex - 1;
      const prev = findEnabledIndex(items, from, -1, wrap);
      if (prev !== -1) {
        focusIndex(items, prev);
      }
    } else if (isHome) {
      focusFirst();
    } else if (isEnd) {
      focusLast();
    }
    e.preventDefault();
  }, [getCurrentIndex, getItems, wrap, orientation, isRtl, hasHomeEnd, hasCaretGuard, findEnabledIndex, focusIndex, focusFirst, focusLast, onEscape]);
  return {
    listRef,
    handleKeyDown,
    handleFocus,
    focusItem,
    focusFirst,
    focusLast
  };
}